import { useEffect, useRef } from "react";

interface PageHeroProps {
  tag: string;
  title: string;
  sub: string;
  bgWord: string;
  bgImage: string;
}

export default function PageHero({ tag, title, sub, bgWord, bgImage }: PageHeroProps) {
  const tagRef = useRef<HTMLSpanElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);
  const subRef = useRef<HTMLParagraphElement>(null);
  const numRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const gridLinesRef = useRef<(HTMLDivElement | null)[]>([]);
  const hLineRef = useRef<HTMLDivElement>(null);

  // Entrance animation
  useEffect(() => {
    const items = [tagRef.current, titleRef.current, lineRef.current, subRef.current];
    items.forEach((el, i) => {
      if (!el) return;
      el.style.opacity = "0";
      el.style.transform = "translateY(32px)";
      setTimeout(() => {
        if (!el) return;
        el.style.transition = "opacity 1s cubic-bezier(0.22,1,0.36,1), transform 1s cubic-bezier(0.22,1,0.36,1)";
        el.style.opacity = "1";
        el.style.transform = "translateY(0)";
      }, i * 120);
    });
  }, []);

  // Grid lines grow animation on mount
  useEffect(() => {
    gridLinesRef.current.forEach((el, i) => {
      if (!el) return;
      el.style.height = "0%";
      el.style.top = "0";
      el.style.bottom = "auto";
      el.style.opacity = "1";
      const delay = i * 0.1 + 0.1;
      setTimeout(() => {
        if (!el) return;
        el.style.transition = `height 1.4s cubic-bezier(0.16,1,0.3,1)`;
        el.style.height = "100%";
      }, delay * 1000);
      // Fade out after draw completes
      setTimeout(() => {
        if (!el) return;
        el.style.transition = "opacity 1.2s ease";
        el.style.opacity = "0";
      }, (delay + 1.4 + 0.3) * 1000);
    });

    const hLine = hLineRef.current;
    if (hLine) {
      hLine.style.width = "0%";
      hLine.style.left = "0";
      hLine.style.right = "auto";
      hLine.style.opacity = "1";
      setTimeout(() => {
        hLine.style.transition = "width 1.6s cubic-bezier(0.16,1,0.3,1)";
        hLine.style.width = "100%";
      }, 550);
      // Fade out after draw completes
      setTimeout(() => {
        if (!hLine) return;
        hLine.style.transition = "opacity 1.2s ease";
        hLine.style.opacity = "0";
      }, (0.55 + 1.6 + 0.3) * 1000);
    }
  }, []);

  // Scroll: bg word scale + content slide up + fade
  useEffect(() => {
    const onScroll = () => {
      const section = sectionRef.current;
      if (!section) return;
      const rect = section.getBoundingClientRect();
      const progress = Math.max(0, Math.min(1, -rect.top / (rect.height * 0.85)));

      // Background word scale
      const numEl = numRef.current;
      if (numEl) {
        numEl.style.transform = `scale(${1 + progress * 2.2})`;
        numEl.style.opacity = `${Math.min(0.04 + progress * 0.1, 0.13)}`;
      }

      // Content slide up + fade
      const earlyProgress = Math.max(0, Math.min(1, -rect.top / (rect.height * 0.05)));
      const translateY = earlyProgress * -1000;
      const opacity = Math.max(0, 1 - earlyProgress * 15.0);
      const contentItems = [tagRef.current, titleRef.current, lineRef.current, subRef.current];
      contentItems.forEach((el) => {
        if (!el) return;
        el.style.transform = `translateY(${translateY}px)`;
        el.style.opacity = `${opacity}`;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const gridPositions = [16.6, 33.3, 50, 66.6, 83.3];

  return (
    <section
      ref={sectionRef}
      className="relative flex flex-col justify-end overflow-hidden"
      style={{ minHeight: "clamp(280px, 50vw, 580px)", background: "#ffffff" }}
    >
      {/* Grid lines — draw from top */}
      <div className="absolute inset-0 pointer-events-none">
        {gridPositions.map((p, i) => (
          <div
            key={p}
            ref={el => { gridLinesRef.current[i] = el; }}
            className="absolute top-0 w-px"
            style={{ left: `${p}%`, background: "rgba(0,0,0,0.09)", height: "0%" }}
          />
        ))}
        <div
          ref={hLineRef}
          className="absolute h-px"
          style={{ top: "62%", left: 0, background: "rgba(0,0,0,0.09)", width: "0%" }}
        />
      </div>

      {/* Big background word — scroll scale */}
      <div
        ref={numRef}
        className="absolute right-0 bottom-0 select-none pointer-events-none"
        style={{
          fontFamily: "'Barlow', sans-serif",
          fontWeight: 700,
          fontSize: "clamp(180px, 28vw, 380px)",
          lineHeight: 0.85,
          letterSpacing: "-0.05em",
          color: "transparent",
          WebkitTextStroke: "1px rgba(0,0,0,0.08)",
          opacity: 0.04,
          transformOrigin: "right bottom",
          willChange: "transform, opacity",
          userSelect: "none",
        }}
      >
        {bgWord}
      </div>

      {/* Content */}
      <div className="relative z-10 px-4 md:px-10 lg:px-16 pb-8 md:pb-16 pt-16 md:pt-24 text-center">
        <span
          ref={tagRef}
          className="block text-sm tracking-[0.35em] md:tracking-[0.5em] font-medium mb-3 md:mb-6"
          style={{ fontFamily: "'Barlow', sans-serif", color: "#c00000" }}
        >
          {tag}
        </span>
        <h1
          ref={titleRef}
          className="font-bold leading-none mb-4 md:mb-8"
          style={{
            fontFamily: "'Barlow', sans-serif",
            fontSize: "clamp(48px, 10vw, 120px)",
            color: "#111111",
            letterSpacing: "-0.03em",
            willChange: "transform, opacity",
            transformOrigin: "center bottom",
          }}
        >
          {title}
        </h1>
        <div
          ref={lineRef}
          className="mx-auto mb-4 md:mb-8"
          style={{ width: "40px", height: "3px", background: "#c00000", transformOrigin: "center" }}
        />
        <p
          ref={subRef}
          className="text-base leading-loose max-w-sm md:max-w-lg mx-auto"
          style={{ fontFamily: "'Noto Sans JP', sans-serif", color: "#111111" }}
        >
          {sub}
        </p>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: "#e8e8e8" }} />
    </section>
  );
}